import {
	supabase,
	SUPABASE_URL,
} from "@shared/integrations/supabase/client.ts";

let hasChecked = false;

const formatSessionStatus = (hasSession: boolean) =>
	hasSession ? "active session detected" : "no active session";

export type SupabaseConnectionResult =
	| { ok: true; hasSession: boolean }
	| { ok: false; error: Error };

const normalizeError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

export const checkSupabaseConnection =
	async (): Promise<SupabaseConnectionResult> => {
		try {
			const { data, error } = await supabase.auth.getSession();

			if (error) {
				throw error;
			}

			return { ok: true, hasSession: Boolean(data.session) };
		} catch (error) {
			return { ok: false, error: normalizeError(error) };
		}
	};

/**
 * Performs a lightweight auth call so we log the Supabase health status to the console.
 * This runs once at boot so local devs immediately see whether env vars / network are OK.
 */
export const logSupabaseConnectionStatus = async () => {
	if (hasChecked) return;
	hasChecked = true;

	const result = await checkSupabaseConnection();

	if (result.ok) {
		const sessionState = formatSessionStatus(result.hasSession);
		console.info(
			`[Supabase] Connection successful (${sessionState}) to ${SUPABASE_URL}.`,
		);
		return;
	}

	console.error(
		`[Supabase] Connection to ${SUPABASE_URL} failed. Check SUPABASE_URL/keys or network access.`,
		result.error,
	);
};

/**
 * For scenarios (like tests) where we want to reset the guard.
 */
export const _resetSupabaseConnectionStatusLog = () => {
	hasChecked = false;
};
